import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { IShiftModal } from 'pages/Shifts/ShiftsModal'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllShiftsSingleResponse,
  IGetWorkScheduleModel,
  IShiftsPostPatchResponse,
  IWorkScheduleModelPostPatchRequest,
} from 'types/api/shiftsApi.interface'
import { IShiftsSlice, ISingleShift } from 'types/shifts.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors, formatParams, getParams } from 'utils/helpers'

const initialState: IShiftsSlice = {
  // TODO ispitai oblike shifta
  isLoading: false,
  shifts: [],
  singleShift: {
    color: null,
    companyId: null,
    id: null,
    name: '',
    info: '',
    absence: false,
    absenceType: null,
    shiftStart: null,
    minutes: null,
  },
  totalCount: 0,
  totalPages: 0,
  workScheduleModels: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
}

export const addShift = createAsyncThunk(
  'shifts/addShift',
  async (shift: IShiftModal, thunkAPI) => {
    try {
      const req: ISingleShift = {
        name: shift.name,
        info: shift.info,
        absenceType: shift.absenceType.value == 6 ? null : shift.absenceType.value,
        shiftStart: shift.absenceType.value == 6 ? shift.range.shiftStart : null,
        minutes: shift.absenceType.value == 6 ? shift.range.minutes : null,
        color: shift.color.includes('#') ? shift.color.slice(1) : shift.color,
      }
      const resp: AxiosResponse<IShiftsPostPatchResponse> = await baseUrl.post(
        '/shifts',
        req,
        thunkAPI,
      )

      await thunkAPI.dispatch(getShifts(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editShift = createAsyncThunk(
  'shifts/editShift',
  async (shift: IShiftModal, thunkAPI) => {
    try {
      const { id } = shift
      const req: ISingleShift = {
        name: shift.name,
        info: shift.info,
        absenceType: shift.absenceType.value == 6 ? null : shift.absenceType.value,
        shiftStart: shift.absenceType.value == 6 ? shift.range.shiftStart : null,
        minutes: shift.absenceType.value == 6 ? shift.range.minutes : null,
        color: shift.color.includes('#') ? shift.color.slice(1) : shift.color,
      }
      const resp: AxiosResponse<IShiftsPostPatchResponse> = await baseUrl.patch(
        '/shifts/' + id,
        req,
        thunkAPI,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editWorkScheduleModel = createAsyncThunk(
  'work-schedule-models/editModel',
  async (model: IWorkScheduleModelPostPatchRequest, thunkAPI) => {
    try {
      const req = { ...model }
      const { id } = req
      delete req.id
      await baseUrl.patch('work-schedule/model/' + id, req, thunkAPI)
      return { ...model, id }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getShifts = createAsyncThunk('shifts/getShifts', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IGetAllShiftsSingleResponse>> = await baseUrl.get(
      'shifts',
      {
        params,
      },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getWorkScheduleModels = createAsyncThunk(
  'work-schedule-models/getModels',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetWorkScheduleModel>> = await baseUrl.get(
        'work-schedule/model',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addWorkScheduleModel = createAsyncThunk(
  'work-schedule-models/addModel',
  async (shift: IWorkScheduleModelPostPatchRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetWorkScheduleModel> = await baseUrl.post(
        '/work-schedule/model',
        shift,
        thunkAPI,
      )
      await thunkAPI.dispatch(getWorkScheduleModels(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteShift = createAsyncThunk('shifts/deleteShift', async (id: number, thunkAPI) => {
  try {
    await baseUrl.delete('shifts/' + id, thunkAPI)
    await thunkAPI.dispatch(getShifts(getParams({ location })))
    return id
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteWorkScheduleModel = createAsyncThunk(
  'work-schedule-models/deleteModel',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('/work-schedule/model/' + id, thunkAPI)
      await thunkAPI.dispatch(getWorkScheduleModels(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const shiftsSlice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    clearShiftsList: (state) => {
      state.shifts = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearWorkScheduleModels: (state) => {
      state.workScheduleModels.data = []
      state.workScheduleModels.totalCount = 0
      state.workScheduleModels.totalPages = 0
    },
  },
  extraReducers: {
    [getShifts.pending.type]: (state) => {
      state.isLoading = true
    },
    [getShifts.fulfilled.type]: (
      state: IShiftsSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllShiftsSingleResponse> },
    ) => {
      state.shifts = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.isLoading = false
    },
    [getShifts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getWorkScheduleModels.pending.type]: (state) => {
      state.isLoading = true
    },
    [getWorkScheduleModels.fulfilled.type]: (
      state: IShiftsSlice,
      { payload }: { payload: IPaginatedResponse<IGetWorkScheduleModel> },
    ) => {
      state.workScheduleModels.data = payload.data
      state.workScheduleModels.totalCount = payload.totalCount
      state.workScheduleModels.totalPages = payload.totalPages
      state.isLoading = false
    },
    [getWorkScheduleModels.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addShift.fulfilled.type]: (state: IShiftsSlice) => {
      state.isLoading = false
      toast.success('Shift added')
    },
    [addShift.pending.type]: (state) => {
      state.isLoading = true
    },
    [addShift.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addWorkScheduleModel.fulfilled.type]: (state: IShiftsSlice) => {
      state.isLoading = false
      toast.success('Work Schedule Model added')
    },
    [addWorkScheduleModel.pending.type]: (state) => {
      state.isLoading = true
    },
    [addWorkScheduleModel.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editShift.pending.type]: (state) => {
      state.isLoading = true
    },
    [editShift.fulfilled.type]: (
      state: IShiftsSlice,
      { payload }: { payload: IShiftsPostPatchResponse },
    ) => {
      const shiftIndex = state.shifts.findIndex((shift) => shift.id == payload.id)
      state.shifts[shiftIndex] = payload
      state.isLoading = false
      toast.success('Shift updated')
    },
    [editShift.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editWorkScheduleModel.pending.type]: (state) => {
      state.isLoading = true
    },
    [editWorkScheduleModel.fulfilled.type]: (state: IShiftsSlice, { payload }: { payload }) => {
      // TODO Tip
      const modelIndex = state.workScheduleModels.data.findIndex((m) => m.id == payload.id)
      state.workScheduleModels.data[modelIndex] = payload
      state.isLoading = false
      toast.success('Model updated')
    },
    [editWorkScheduleModel.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteShift.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteShift.fulfilled.type]: (state: any) => {
      state.isLoading = false
      toast.success('Shift deleted')
    },
    [deleteShift.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteWorkScheduleModel.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteWorkScheduleModel.fulfilled.type]: (state: IShiftsSlice) => {
      state.isLoading = false
      toast.success('Model deleted')
    },
    [deleteWorkScheduleModel.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})
export const { clearShiftsList, clearWorkScheduleModels } = shiftsSlice.actions

export default shiftsSlice.reducer
